/**
 * Utility functions for Similar Players feature
 *
 * Provides score extraction, badge type mapping, and badge configuration
 * based on sort field selection.
 */

import {
  SimilarPlayerSortField,
  SimilarPlayersPlayerResultFragment,
  SortField,
} from "@/generated/graphql";

import { calculateMonthsRemaining } from "./playerPageHeaderUtils";

/**
 * Mapping from SimilarPlayerSortField to the corresponding bioData field
 */
const SORT_FIELD_TO_BIO_DATA_KEY: Record<
  SimilarPlayerSortField,
  | keyof NonNullable<
      NonNullable<SimilarPlayersPlayerResultFragment["player"]>["bioData"]
    >
  | null
> = {
  [SimilarPlayerSortField.Gpr]: "gpr",
  [SimilarPlayerSortField.FitScore]: "fitScore",
  [SimilarPlayerSortField.TeamStyleFit]: "teamStyleFit",
  [SimilarPlayerSortField.Passing]: "passingTransGlobalCategoricalScore",
  [SimilarPlayerSortField.Shooting]: "shotLocationTransGlobalCategoricalScore",
  [SimilarPlayerSortField.Carrying]: "carryingTransGlobalCategoricalScore",
  [SimilarPlayerSortField.Dribbling]: "dribblingTransGlobalCategoricalScore",
  [SimilarPlayerSortField.GkPassing]: "gkPassingTransCategoricalScore",
  [SimilarPlayerSortField.GkPositioning]: "gkPositioningTransCategoricalScore",
  [SimilarPlayerSortField.GkShotStopping]:
    "gkShotStoppingTransCategoricalScore",
  [SimilarPlayerSortField.Gpm]: null, // Not in bioData
  [SimilarPlayerSortField.MonthsRemaining]: null, // Not a score
  [SimilarPlayerSortField.PlayerValuation]: null, // Not a score for badge
  [SimilarPlayerSortField.GeminiPlayerValuation]: null, // Not selected by the fragment
  [SimilarPlayerSortField.SimilarityScore]: null, // Use similarityScore directly
};

/**
 * Mapping from SimilarPlayerSortField to SortField for badge type
 */
const SORT_FIELD_TO_BADGE_TYPE: Record<SimilarPlayerSortField, SortField> = {
  [SimilarPlayerSortField.Gpr]: SortField.Gpr,
  [SimilarPlayerSortField.FitScore]: SortField.FitScore,
  [SimilarPlayerSortField.TeamStyleFit]: SortField.TeamStyleFit,
  [SimilarPlayerSortField.Passing]: SortField.Passing,
  [SimilarPlayerSortField.Shooting]: SortField.Shooting,
  [SimilarPlayerSortField.Carrying]: SortField.Carrying,
  [SimilarPlayerSortField.Dribbling]: SortField.Dribbling,
  [SimilarPlayerSortField.GkPassing]: SortField.GkPassing,
  [SimilarPlayerSortField.GkPositioning]: SortField.GkPositioning,
  [SimilarPlayerSortField.GkShotStopping]: SortField.GkShotStopping,
  [SimilarPlayerSortField.Gpm]: SortField.Gpm,
  [SimilarPlayerSortField.MonthsRemaining]: SortField.MonthsRemaining,
  [SimilarPlayerSortField.PlayerValuation]: SortField.PlayerValuation,
  [SimilarPlayerSortField.GeminiPlayerValuation]:
    SortField.GeminiPlayerValuation,
  [SimilarPlayerSortField.SimilarityScore]: SortField.Gpr, // 0-100 scale like GPR
};

/**
 * Score extractors for sort fields not mapped to a single bioData key.
 * SimilarityScore, PlayerValuation, and MonthsRemaining each need
 * custom extraction logic rather than a direct bioData field lookup.
 *
 * GeminiPlayerValuation deliberately has no extractor: the fragment does not
 * select `geminiPlayerValuation` and no sort UI offers the field yet — its
 * entries in the Records above exist only to satisfy exhaustiveness after
 * the enum landed in the schema. Add the extractor (and the fragment field)
 * with the feature that ships the sort option.
 */
const UNMAPPED_SCORE_EXTRACTORS: Partial<
  Record<
    SimilarPlayerSortField,
    (i: SimilarPlayersPlayerResultFragment) => number | null
  >
> = {
  [SimilarPlayerSortField.SimilarityScore]: i => i.similarityScore ?? null,
  [SimilarPlayerSortField.PlayerValuation]: i =>
    i.player?.bioData?.playerValuation ?? null,
  [SimilarPlayerSortField.MonthsRemaining]: i =>
    calculateMonthsRemaining(i.player?.bioData?.contractExpires),
};

/**
 * Extracts the appropriate score from a similar player result based on sort field.
 * Each unmapped sort field (PlayerValuation, MonthsRemaining, SimilarityScore)
 * returns its specific value so the badge displays the correct metric.
 * @param item - The similar player result fragment
 * @param sortField - The currently selected sort field
 * @returns The score value to display, or null when the metric is unavailable for this player
 */
export function getScoreForSortField(
  item: SimilarPlayersPlayerResultFragment,
  sortField: SimilarPlayerSortField
): number | null {
  const bioDataKey = SORT_FIELD_TO_BIO_DATA_KEY[sortField];

  if (bioDataKey) {
    const value = item.player?.bioData?.[bioDataKey];
    return typeof value === "number" ? value : null;
  }

  const extractor = UNMAPPED_SCORE_EXTRACTORS[sortField];

  return extractor
    ? extractor(item)
    : (item.similarityScore ?? item.player?.bioData?.gpr ?? 0);
}

/**
 * Gets the badge type for a given sort field
 * @param sortField - The currently selected sort field
 * @returns The SortField enum value for the badge component
 */
export function getBadgeTypeForSortField(
  sortField: SimilarPlayerSortField
): SortField {
  return SORT_FIELD_TO_BADGE_TYPE[sortField];
}

/** Badge configuration for color-coding and scale */
interface BadgeConfig {
  readonly min: number;
  readonly max: number;
  readonly isHigherBetter: boolean;
  readonly isDefaultStyle?: boolean;
}

/** Default config for 0-100 score fields (GPR, categorical scores, etc.) */
const SCORE_BADGE_CONFIG: BadgeConfig = {
  min: 30,
  max: 100,
  isHigherBetter: true,
};

/**
 * Badge min/max/color configuration per sort field.
 * Transfer value uses default style (no color coding).
 * Months remaining uses inverted scale (fewer months = better).
 */
const SORT_FIELD_TO_BADGE_CONFIG: Record<SimilarPlayerSortField, BadgeConfig> =
  {
    [SimilarPlayerSortField.Gpr]: SCORE_BADGE_CONFIG,
    [SimilarPlayerSortField.FitScore]: SCORE_BADGE_CONFIG,
    [SimilarPlayerSortField.TeamStyleFit]: SCORE_BADGE_CONFIG,
    [SimilarPlayerSortField.Passing]: SCORE_BADGE_CONFIG,
    [SimilarPlayerSortField.Shooting]: SCORE_BADGE_CONFIG,
    [SimilarPlayerSortField.Carrying]: SCORE_BADGE_CONFIG,
    [SimilarPlayerSortField.Dribbling]: SCORE_BADGE_CONFIG,
    [SimilarPlayerSortField.GkPassing]: SCORE_BADGE_CONFIG,
    [SimilarPlayerSortField.GkPositioning]: SCORE_BADGE_CONFIG,
    [SimilarPlayerSortField.GkShotStopping]: SCORE_BADGE_CONFIG,
    [SimilarPlayerSortField.Gpm]: SCORE_BADGE_CONFIG,
    [SimilarPlayerSortField.SimilarityScore]: SCORE_BADGE_CONFIG,
    [SimilarPlayerSortField.PlayerValuation]: {
      min: 0,
      max: 1_000_000_000,
      isHigherBetter: true,
      isDefaultStyle: true,
    },
    [SimilarPlayerSortField.GeminiPlayerValuation]: {
      min: 0,
      max: 1_000_000_000,
      isHigherBetter: true,
      isDefaultStyle: true,
    },
    [SimilarPlayerSortField.MonthsRemaining]: {
      min: 18,
      max: 6,
      isHigherBetter: true,
    },
  };

/**
 * Returns badge display config (min, max, scale direction, style) for a sort field.
 * Allows the badge component to color-code values correctly per metric type.
 * @param sortField - The currently selected sort field
 * @returns Badge configuration with min, max, isHigherBetter, and optional isDefaultStyle
 */
export function getBadgeConfigForSortField(
  sortField: SimilarPlayerSortField
): BadgeConfig {
  return SORT_FIELD_TO_BADGE_CONFIG[sortField];
}
